/* PlayMix: auto-apply the FIRST code in the cart drawer, with a typing animation and a "saved" banner.
   Runs once per browser session, so a shopper who removes the code is not overridden. */
(() => {
  const CODE = 'FIRST';
  const FLAG = 'pmFirstAuto';
  const seen = () => { try { return sessionStorage.getItem(FLAG) === '1'; } catch (e) { return false; } };
  const mark = () => { try { sessionStorage.setItem(FLAG, '1'); } catch (e) {} };
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let busy = false;

  const style = document.createElement('style');
  style.textContent = `
    .pm-first-hl{animation:pmFirstGlow 1.2s ease-out 2}
    @keyframes pmFirstGlow{0%{box-shadow:0 0 0 0 rgba(255,74,61,.7)}100%{box-shadow:0 0 0 12px rgba(255,74,61,0)}}
    .pm-first-toast{display:flex;align-items:center;gap:10px;margin:10px 0 4px;padding:10px 14px;border:2px solid #15131A;border-radius:12px;background:#FFC93C;color:#15131A;font:600 14px/1.3 Inter,system-ui,sans-serif;box-shadow:3px 3px 0 #15131A;animation:pmFirstIn .45s cubic-bezier(.2,1.4,.4,1) both}
    .pm-first-toast b{font-family:'Anton',Impact,sans-serif;font-weight:400;font-size:18px;letter-spacing:.02em;color:#C8321F}
    .pm-first-toast code{font:700 12px 'DM Mono',monospace;background:#15131A;color:#FFC93C;padding:2px 6px;border-radius:4px}
    .pm-first-toast i{font-style:normal;font-size:20px}
    @keyframes pmFirstIn{from{opacity:0;transform:translateY(8px) scale(.96)}to{opacity:1;transform:none}}
    @media (prefers-reduced-motion:reduce){.pm-first-hl,.pm-first-toast{animation:none}}`;
  document.head.append(style);

  const money = (cents) => 'Rs. ' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const hasFirst = (cart) => (cart.discount_codes || []).some((d) => d.code.toUpperCase() === CODE && d.applicable !== false);

  function showToast(drawer, saved) {
    drawer.querySelector('.pm-first-toast')?.remove();
    const anchor = drawer.querySelector('cart-discount') || drawer.querySelector('input[name="discount"]')?.closest('details');
    if (!anchor) return;
    const t = document.createElement('div');
    t.className = 'pm-first-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = `<i aria-hidden="true">🎉</i><span>Code <code>${CODE}</code> applied, extra 15% off${saved > 0 ? ` &middot; you save <b>${money(saved)}</b>` : ''}</span>`;
    anchor.before(t);
  }

  /* Apply the code through the Cart API when the drawer's discount form isn't available */
  const applyDirect = () => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: CODE }),
  });

  async function run(drawer) {
    if (busy) return;
    busy = true;
    try {
      /* The drawer can open before the added item lands, so wait briefly for it */
      let cart = await getCart();
      for (let i = 0; i < 10 && !cart.item_count; i++) {
        await sleep(400);
        cart = await getCart();
      }
      if (!cart.item_count) return;
      await sleep(600);
      if (hasFirst(cart)) { showToast(drawer, cart.total_discount); return; }
      if (seen()) return;
      mark();
      const input = drawer.querySelector('input[name="discount"]');
      if (!input || !input.form) { await applyDirect(); return; }

      const details = input.closest('details');
      if (details && !details.open) details.open = true;
      input.classList.add('pm-first-hl');
      input.focus({ preventScroll: true });
      input.value = '';
      for (const ch of CODE) {
        input.value += ch;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        await sleep(reduced ? 0 : 140);
      }
      await sleep(reduced ? 0 : 250);
      input.form.requestSubmit();

      for (let i = 0; i < 12; i++) {
        await sleep(400);
        cart = await getCart();
        if (hasFirst(cart)) break;
      }
      if (hasFirst(cart)) showToast(drawer, cart.total_discount);
    } catch (e) {
      /* never block the cart on this enhancement */
    } finally {
      busy = false;
    }
  }

  function watch() {
    /* The cart lives inside a <theme-drawer> that gets an [open] attribute while shown.
       Watch the whole page, since the drawer can be rendered after this script runs. */
    const openCart = () => document.querySelector('cart-drawer-component')?.closest('theme-drawer[open]') || null;
    let wasOpen = !!openCart();
    if (wasOpen) setTimeout(() => { const el = openCart(); if (el) run(el); }, 800);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        const el = openCart();
        if (el && !wasOpen) setTimeout(() => run(el), reduced ? 0 : 450);
        wasOpen = !!el;
      });
    }).observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['open'] });

    /* Add-to-cart can also open the Razorpay side cart instead of the theme drawer,
       so apply the code straight to the cart after any add, once per session. */
    if (!('PerformanceObserver' in window)) return;
    new PerformanceObserver((list) => {
      if (!list.getEntries().some((e) => /\/cart\/add(\.js)?(\?|$)/.test(e.name))) return;
      setTimeout(async () => {
        const el = openCart();
        if (el && el.querySelector('input[name="discount"]')) return run(el);
        if (seen() || busy) return;
        try {
          const cart = await getCart();
          if (!cart.item_count || hasFirst(cart)) return;
          mark();
          await applyDirect();
        } catch (e) {}
      }, 900);
    }).observe({ type: 'resource', buffered: false });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
